import Channel from '@/Enums/Channel.enum';
import { IImpressionCalculation } from '@/Interfaces/Forecaster';
import TabPanel from '@/components/TabPanel';
import TabButton from '@/pages/Forecaster/Estimation/EstimationTabs/TabButton';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { SyntheticEvent, memo, useMemo } from 'react';
import { TabName } from '../TabName.enum';
import { BASE_TAB_ID, TABS } from '../constants';
import ForecastTab from './ForecastTab';

interface IProps {
  isExpanded: boolean;
  activeTab: TabName;
  setActiveTab: (tab: TabName) => void;
  loading: boolean;
  forecast: IImpressionCalculation | null;
  showReach: boolean;
  channel?: Channel;
  setDaysInRange: (daysInRange: number) => void;
}

const ForecasterWidgetView = memo(
  ({ isExpanded, activeTab, setActiveTab, loading, forecast, showReach, channel, setDaysInRange }: IProps) => {
    const handleTabChange = (_: SyntheticEvent, idx: TabName) => {
      setActiveTab(idx);
    };

    const tabsToRender = useMemo(
      () => (isExpanded ? TABS : [TABS.find(({ id }) => id === activeTab) ?? TABS[0]]),
      [isExpanded, activeTab],
    );

    const tabContentProps = useMemo(
      () => ({ loading, forecast: forecast ?? undefined, showReach, channel, handleDaysInRangeChange: setDaysInRange }),
      [loading, forecast, showReach, channel, setDaysInRange],
    );

    return (
      <div
        className={`flex flex-col gap-1 border rounded-md shrink-0 ${showReach ? 'min-w-[400px]' : 'min-w-[300px]'}`}
      >
        {isExpanded && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: '44px',
              background: '#E9EDEB',
              padding: '4px',
              borderRadius: '6px',
              width: '100%',
              [`& .${tabClasses.root}`]: {
                padding: '10px 0',
                justifyContent: 'flex-start',
                paddingLeft: '16px',
                paddingRight: '16px',
                minHeight: 'auto',
                minWidth: 'auto',
                fontWeight: 700,
              },
              [`& .${tabClasses.selected}`]: {
                background: 'white',
              },
            }}
            slotProps={{
              indicator: {
                hidden: true,
              },
            }}
          >
            {TABS.map(tab => (
              <Tab
                key={tab.id}
                value={tab.id}
                component={TabButton}
                label={tab.name}
                {...a11yProps(tab.id, BASE_TAB_ID)}
              />
            ))}
          </Tabs>
        )}
        {tabsToRender.map(tab => (
          <TabPanel className='w-full h-full' key={tab.id} baseTabId={BASE_TAB_ID} value={activeTab} index={tab.id}>
            <ForecastTab
              {...tabContentProps}
              tabNamePrefix={tab.name as TabName}
              customDateRangeVariant={tab.id === TabName.CUSTOM}
            />
          </TabPanel>
        ))}
      </div>
    );
  },
);

export default ForecasterWidgetView;
